// マイク・スピーカーと AudioWorklet の配線 (メインスレッド側)。
//
//   getUserMedia (マイク) → MediaStreamSource → AudioWorkletNode("telephone-processor") → destination (スピーカー)
//
// **マイクが無い端末でも「聞くだけ」で入室できる** (タスク #35)。getUserMedia が失敗したら
// MediaStreamSource を繋がずに進める。Worklet の process() には入力が来ないので音声フレームを
// 作らず、受信と再生だけが動く。試聴では「相手の声がどう聞こえるか」を判定するので、
// 聴く側にマイクは要らない。
//
// WASM はメインスレッドで fetch + compile し、WebAssembly.Module を processorOptions で Worklet に渡す
// (Worklet の中では fetch が使えないため)。

import wasmUrl from "../../core/pkg/telephone_client_core_bg.wasm?url";
import workletUrl from "./worklet.ts?worker&url";
import type { ToMain, ToWorklet, WorkletProfile } from "./messages.ts";

/** プラットフォーム層とやり取りする標本化周波数。core の INPUT_SAMPLE_RATE_HZ と同じ。 */
export const SAMPLE_RATE_HZ = 48_000;

export interface AudioEvents {
  onFrame: (bytes: Uint8Array) => void;
  onStats: (stats: {
    json: string;
    speaking: boolean;
    participants: number[];
    speakingParticipants: number[];
    /** 静かなときの音量（dBFS）。音声検出のゲージに出す。 */
    noiseFloorDb: number;
    /** いま効いている発話検出の境目（dBFS）。 */
    vadThresholdDb: number;
    /** いまの入力の大きさ（dBFS。コアで平滑化済み。タスク #178）。 */
    inputLevelDb: number;
  }) => void;
  /** 音声側の異常（Worklet の中の例外・processorerror）。画面に出すために渡す。 */
  onError: (where: string, message: string) => void;
}

export class Audio {
  private constructor(
    private readonly context: AudioContext,
    /** マイクの音源。**無い端末では null**（聞くだけ）。 */
    private readonly stream: MediaStream | null,
    private readonly node: AudioWorkletNode,
    /** マイクが無く「聞くだけ」で動いているか。 */
    readonly listenOnly: boolean,
    /** マイクを使えなかった理由（`listenOnly` のときだけ入る）。 */
    readonly micError: string | null,
  ) {}

  /**
   * マイクと Worklet を用意する。`profile` は入室応答 (joined.audio_profile) の値。
   * **入室してからでないと呼べない** (設定が分からないと Engine を作れないため)。
   */
  static async start(profile: WorkletProfile, events: AudioEvents): Promise<Audio> {
    // ブラウザの前処理は ADR-0003 の方針どおり: エコーキャンセル・ノイズ抑制はブラウザのものをオンのまま使う
    // (自前のノイズ抑制は #26 で差し替え口に入る)。自動ゲインは自前の手動ゲインと二重にならないよう切る。
    // マイクは**取れなくても続ける**（聞くだけで入室する。タスク #35）。
    let stream: MediaStream | null = null;
    let micError: string | null = null;
    if (!navigator.mediaDevices?.getUserMedia) {
      micError =
        "このブラウザではマイクを使えない（getUserMedia が無い）。" +
        "http:// で開いていると使えないので https:// か localhost で開く";
    } else {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 1,
            sampleRate: SAMPLE_RATE_HZ,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
          },
          video: false,
        });
      } catch (e) {
        micError = e instanceof Error ? `${e.name}: ${e.message}` : String(e);
      }
    }

    // AudioContext は 48 kHz で作る。コアが 48 kHz 前提で、そこから
    // コーデックの周波数へ整数比で変換するため（44.1 kHz からは変換できない）。
    let context: AudioContext;
    try {
      context = new AudioContext({ sampleRate: SAMPLE_RATE_HZ, latencyHint: "interactive" });
    } catch (e) {
      stream?.getTracks().forEach((t) => t.stop());
      throw new Error(
        `AudioContext を ${SAMPLE_RATE_HZ} Hz で作れない: ` +
          `${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}` +
          "\n        → OS の音声出力・入力を 48000 Hz にすると通ることがある" +
          "（Windows: サウンド > デバイスのプロパティ > 詳細 / macOS: Audio MIDI 設定）",
      );
    }
    if (context.sampleRate !== SAMPLE_RATE_HZ) {
      const actual = context.sampleRate;
      stream?.getTracks().forEach((t) => t.stop());
      await context.close();
      throw new Error(
        `AudioContext が ${SAMPLE_RATE_HZ} Hz にならない（${actual} Hz になった）` +
          "\n        → この実装は 48000 Hz 前提（そこからコーデックの周波数へ整数比で変換する）" +
          "\n        → OS の音声出力・入力を 48000 Hz に変えてページを再読み込みする" +
          "（Windows: サウンド > デバイスのプロパティ > 詳細 / macOS: Audio MIDI 設定）",
      );
    }

    let wasmModule: WebAssembly.Module;
    try {
      [wasmModule] = await Promise.all([
        WebAssembly.compileStreaming(fetch(wasmUrl)),
        context.audioWorklet.addModule(workletUrl),
      ]);
    } catch (e) {
      stream?.getTracks().forEach((t) => t.stop());
      await context.close();
      throw new Error(
        `音声処理の読み込みに失敗（WASM ${wasmUrl} または Worklet ${workletUrl}）: ` +
          `${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`,
      );
    }

    const node = new AudioWorkletNode(context, "telephone-processor", {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
      processorOptions: { wasmModule, profile },
    });
    node.port.onmessage = (e: MessageEvent<ToMain>) => {
      const m = e.data;
      if (m.type === "frame") events.onFrame(m.bytes);
      else if (m.type === "stats") events.onStats(m);
      else if (m.type === "error") events.onError(m.where, m.message);
    };
    // Worklet が例外で止まったとき。**内容は入っていない**ので、上の "error" を頼りにする
    node.addEventListener("processorerror", () => {
      events.onError(
        "AudioWorklet",
        "音声処理が異常終了した（詳しい内容はブラウザのコンソールに出る）",
      );
    });

    // マイクが無いときは入力を繋がない。Worklet は出力だけ回る（送信フレームは作られない）
    if (stream) context.createMediaStreamSource(stream).connect(node);
    node.connect(context.destination);
    await context.resume();
    return new Audio(context, stream, node, stream === null, micError);
  }

  private send(m: ToWorklet, transfer?: Transferable[]): void {
    if (transfer) this.node.port.postMessage(m, transfer);
    else this.node.port.postMessage(m);
  }

  /** 届いた Datagram を Worklet の Engine へ。 */
  onDatagram(bytes: Uint8Array): void {
    // Datagram の Uint8Array は読み取り側の内部バッファを指すことがあるのでコピーして移譲する
    const copy = bytes.slice();
    this.send({ type: "frame", bytes: copy }, [copy.buffer]);
  }

  setParticipant(id: number): void {
    this.send({ type: "participant", id });
  }

  setGainDb(db: number): void {
    this.send({ type: "gain", db });
  }

  /**
   * 相手 1 人ぶんの受信ゲイン (dB)。**自分の端末でしか効かない** (相手の送るものは変わらない)。
   * `id` は音声フレームの送信元で、MCU では 0 (合成音声) だけが届く。
   */
  setParticipantGainDb(id: number, db: number): void {
    this.send({ type: "participant_gain", id, db });
  }

  /**
   * **退室した送信元のジッタバッファを畳む** (#56 ⑤)。
   *
   * 相手が消えてもパケットは来ないので、放っておくと空のジッタバッファから
   * 取り出し続けて**ジッタバッファ待ちと再生停止が増える**（回線で落ちたわけでは
   * ないのに #52 の指標に混ざる）。退室は制御ストリームで分かるので待たずに畳む。
   *
   * `id` は**音声フレームの送信元**。SFU では参加者の ID と同じだが、
   * **MCU は合成音声 1 本 (0) しか届かない**ので、名簿の ID を渡しても何も起きない。
   */
  removeParticipant(id: number): void {
    this.send({ type: "participant_left", id });
  }

  /**
   * 送信側の帯域制限の下限 (ハイパスのカットオフ。Hz)。
   * **自分が送る音が変わる** ので、相手に聞こえる音にも効く (受信ゲインとは逆向き)。
   */
  setHighpassHz(hz: number): void {
    this.send({ type: "highpass", hz });
  }

  /** 送信側の帯域制限の上限 (ローパスのカットオフ。Hz)。部屋の上限より上は core 側で丸める。 */
  setLowpassHz(hz: number): void {
    this.send({ type: "lowpass", hz });
  }

  /**
   * **自分のミュート**（M1。画面仕様 §3）。**コア側で止める**ので帯域も落ちる。
   *
   * マイクが使えないとき（聞くだけ）は押せない作りにするのが画面の役目。
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    this.send({ type: "muted", muted });
  }

  /** いまミュートしているか。 */
  get isMuted(): boolean {
    return this.muted;
  }

  private muted = false;

  /** **聞く音の大きさ**（受信の全体。dB）。自分の端末だけで効く。 */
  setMasterGainDb(db: number): void {
    this.send({ type: "master_gain", db });
  }

  setVadThresholdDb(db: number): void {
    this.send({ type: "vad", db });
  }

  setDtx(enabled: boolean): void {
    this.send({ type: "dtx", enabled });
  }

  /**
   * **ノイズ抑制の入／切** (#63)。**自分が送る音が変わる**ので相手に聞こえる音に効く。
   *
   * 切ると素通しに戻るので **10 ms の遅れと CPU (実時間の 0.4%) も消える**。
   * 切り替えた瞬間に 10 ms ぶんの継ぎ目が 1 回鳴る (聴き比べのため切れるようにしてある)。
   */
  setDenoise(enabled: boolean): void {
    this.send({ type: "denoise", enabled });
  }

  /**
   * ノイズ抑制の強さ (無声区間 / 有声区間。0.0〜1.0)。
   *
   * 強いほど騒音は減るが、**有声区間を強くすると声が歪む** (DOC-0023 §4-2 の聴感)。
   * 実装は作り直さないので通話中に動かしても継ぎ目は鳴らない。
   */
  setDenoiseStrength(unvoiced: number, voiced: number): void {
    this.send({ type: "denoise_strength", unvoiced, voiced });
  }

  async stop(): Promise<void> {
    this.send({ type: "stop" });
    this.stream?.getTracks().forEach((t) => t.stop());
    this.node.disconnect();
    await this.context.close();
  }

  get baseLatencyMs(): number {
    return Math.round(this.context.baseLatency * 1000);
  }

  /**
   * **実際に使ったマイクと、ブラウザが掛けた音声処理**（2026-10-09 追加）。
   *
   * 2026-10-01（内蔵マイク）と 2026-10-08 の試験で騒音の入り方が正反対になったのに、
   * **どちらもマイクの種類が保存ログに残っていなかった**ため原因を切り分けられなかった
   * （DOC-0026 §0 / タスク #26）。マイクの名前と、要求した指定が通ったかを記録に残す。
   *
   * `label` が空の端末もある（許可の与え方によって名前を伏せる実装がある）。
   */
  get micDescription(): string {
    const track = this.stream?.getAudioTracks()[0];
    if (!track) return "無し（聞くだけ）";
    const s = track.getSettings();
    const got = (key: "echoCancellation" | "noiseSuppression" | "autoGainControl") => {
      const v = s[key];
      return `${key}=${v === undefined ? "不明" : v}`;
    };
    return (
      `「${track.label || "名前なし"}」 / ${got("echoCancellation")} / ` +
      `${got("noiseSuppression")} / ${got("autoGainControl")}`
    );
  }
}
